import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePagination } from "../../shared/hooks/usePagination";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { IJob } from "../../shared/interfaces/job/IJob";
import { JobService } from "./service";
import { Card, CardBody, Col, Nav, Tab } from "react-bootstrap";
import Select from "react-select";
import { CustomPagination } from "../../shared/components/CustomPagination";
import DeleteModal from "../../Common/DeleteModal";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { JobCategoryService } from "../job-categories/service";
import { IJobCategory } from "../../shared/interfaces/job-category/IJobCategory";

const PER_PAGE_DEFAULT = 10;

interface SortableRowProps {
  job: IJob;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onCopy: (id: number) => void;
  t: (key: string) => string;
}

const SortableRow = ({
  job,
  onEdit,
  onDelete,
  onCopy,
  t,
}: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: job.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      <td {...listeners} style={{ cursor: "grab" }}>
        <i className="ti ti-grip-vertical f-18"></i>
      </td>
      <td>
        {job?.name}
        {job?.internalName && (
          <div className="text-muted f-12">{job.internalName}</div>
        )}
      </td>
      <td>{(job as any)?.floorType?.name || "-"}</td>
      <td>{job?.calculationType || "-"}</td>
      <td>{new Date(job.updatedAt).toLocaleDateString()}</td>
      <td className="text-end">
        <ul className="list-inline mb-0">
          <li className="list-inline-item" onClick={() => onEdit(job.id)}>
            <a
              href="#"
              className="avtar avtar-s btn-link-success btn-pc-default"
            >
              <i className="ti ti-edit f-20"></i>
            </a>
          </li>
          <li className="list-inline-item">
            <span
              role="button"
              className="avtar avtar-s btn-link-info btn-pc-default"
              onClick={() => onCopy(job.id)}
            >
              <i className="ti ti-copy f-20"></i>
            </span>
          </li>
          <li className="list-inline-item">
            <span
              role="button"
              className="avtar avtar-s btn-link-danger btn-pc-default"
              onClick={() => onDelete(job.id)}
            >
              <i className="ti ti-trash f-20"></i>
            </span>
          </li>
        </ul>
      </td>
    </tr>
  );
};

const JobList = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState<IJob[] | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<number | null>(null);
  const [jobCategories, setJobCategories] = useState<IJobCategory[]>([]);
  const [activeTab, setActiveTab] = useState<string>(searchParams.get("jobCategory") || "all");
  const navigate = useNavigate();
  const {
    page,
    handlePages,
    pagination,
    totalPages,
    setTotalPages,
    changeFilterHandler,
    goToPreviousPage,
    goToNextPage,
  } = usePagination({
    perPage: PER_PAGE_DEFAULT,
  });
  const entriesPerPageOptions = [
    { label: "10", value: 10 },
    { label: "25", value: 25 },
    { label: "50", value: 50 },
    { label: "100", value: 100 },
    { label: "200", value: 200 },
  ];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && list) {
      const oldIndex = list.findIndex((item) => item.id === active.id);
      const newIndex = list.findIndex((item) => item.id === over.id);

      const newList = arrayMove(list, oldIndex, newIndex);
      setList(newList);

      try {
        const ids = newList.map((item) => item.id);
        await JobService.sort(ids);
        SuccessToast(t("job.sortSuccess"));
      } catch (error) {
        ErrorToast(error);
        fetchData();
      }
    }
  };

  const handleDeleteClick = (id: number) => {
    setJobToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (jobToDelete !== null) {
      try {
        await JobService.delete(jobToDelete);
        SuccessToast(t("job.successDelete"));
        setShowDeleteModal(false);
        setJobToDelete(null);
        fetchData();
      } catch (error) {
        ErrorToast(error);
      }
    }
  };

  const handleCopy = async (id: number) => {
    try {
      await JobService.copy(id);
      SuccessToast(t("job.successCopy"));
      fetchData();
    } catch (error) {
      ErrorToast(error);
    }
  };

  const fetchData = async () => {
    try {
      const params = activeTab === "all"
        ? pagination
        : { ...pagination, jobCategory: Number(activeTab) };
      const response = await JobService.get(params);
      const data = response.data;
      setList(data);
      setTotalPages(response.totalPages);
    } catch (error) {
      ErrorToast(error);
    }
  };

  // Fetch job categories for tabs
  useEffect(() => {
    JobCategoryService.getAll({ page: 1, perPage: 200 })
      .then((response) => setJobCategories(response.data || []))
      .catch(ErrorToast);
  }, []);

  useEffect(() => {
    fetchData();
  }, [pagination, activeTab]);

  const handleTabChange = (tabKey: string | null) => {
    if (tabKey) {
      setActiveTab(tabKey);
      if (tabKey === "all") {
        searchParams.delete("jobCategory");
      } else {
        searchParams.set("jobCategory", tabKey);
      }
      setSearchParams(searchParams);
    }
  };

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody>
            <div className="text-end">
              <button
                className="btn btn-primary text-end"
                onClick={() => navigate("/jobs/create")}
              >
                <i className="ti ti-plus f-18"></i> {t("job.add")}
              </button>
            </div>

            <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
              <Nav variant="tabs" className="mb-3 mt-3">
                <Nav.Item>
                  <Nav.Link eventKey="all">{t("general.all")}</Nav.Link>
                </Nav.Item>
                {jobCategories.map((cat) => (
                  <Nav.Item key={cat.id}>
                    <Nav.Link eventKey={String(cat.id)}>{cat.name}</Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
              <Tab.Content>
                <Tab.Pane eventKey={activeTab}>
                  <div className="d-sm-flex align-items-center mb-3">
                    <ul className="list-inline me-auto my-1">
                      <li className="list-inline-item">
                        <Select
                          placeholder={t("job.entriesPerPage")}
                          options={entriesPerPageOptions}
                          onChange={(selected) =>
                            changeFilterHandler(
                              "perPage",
                              selected?.value ?? PER_PAGE_DEFAULT,
                            )
                          }
                          defaultValue={entriesPerPageOptions.find(
                            (option) => option.value === PER_PAGE_DEFAULT,
                          )}
                          classNamePrefix="react-select"
                        />
                      </li>
                      <span> {t("job.entriesPerPage")}</span>
                    </ul>
                    <ul className="list-inline ms-auto my-1 mt-3">
                      <li className="list-inline-item">
                        <form>
                          <input
                            className="form-control"
                            type="text"
                            placeholder={t("job.searchPlaceholder")}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              changeFilterHandler("term", e.target.value)
                            }
                          />
                        </form>
                      </li>
                    </ul>
                  </div>
                  <div className="table-responsive">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <table className="table table-hover" id="pc-dt-simple-1">
                        <thead>
                          <tr>
                            <th style={{ width: "50px" }}></th>
                            <th>{t("job.name")}</th>
                            <th>{t("job.floorType")}</th>
                            <th>{t("job.calculationType")}</th>
                            <th>{t("job.updatedAt")}</th>
                            <th className="text-end">{t("job.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {list && list.length > 0 && (
                            <SortableContext
                              items={list.map((job) => job.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              {list.map((job: IJob) => (
                                <SortableRow
                                  key={job.id}
                                  job={job}
                                  onEdit={(id) => navigate(`/jobs/edit/${id}`)}
                                  onDelete={handleDeleteClick}
                                  onCopy={handleCopy}
                                  t={t}
                                />
                              ))}
                            </SortableContext>
                          )}
                        </tbody>
                      </table>
                    </DndContext>
                  </div>
                  <CustomPagination
                    page={page}
                    totalPages={totalPages}
                    handlePages={handlePages}
                    goToPreviousPage={goToPreviousPage}
                    goToNextPage={goToNextPage}
                  />
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </CardBody>
        </Card>
      </Col>

      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDeleteId={handleDeleteConfirm}
        name={jobToDelete}
      />
    </React.Fragment>
  );
};

export default JobList;
